import React from 'react';
import { Container, Paper, Typography, Box, Alert, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageBackButton from '../../components/PageBackButton';

interface Section {
  heading: string;
  paragraphs: string[];
}

type LangKey = 'bg' | 'en' | 'nl' | 'tr' | 'pl';

const LAST_UPDATED = '18.08.2026';

const content: Record<LangKey, Section[]> = {
  nl: [
    {
      heading: '1. Algemene bepalingen',
      paragraphs: [
        'Deze Algemene Voorwaarden regelen de relatie tussen MasterBook („het Platform", „wij") en de gebruikers die zich registreren en gebruikmaken van de website masterbook.nl (hierna „Gebruikers").',
        'MasterBook wordt geëxploiteerd door [Naam eigenaar], handelend onder de naam "MasterBook" (eenmanszaak, KVK-nummer: [in te vullen na registratie]), gevestigd te [adres], Nederland.',
        'Het Platform verbindt Klanten die op zoek zijn naar diensten met Vakmensen die dergelijke diensten aanbieden. MasterBook treedt uitsluitend op als bemiddelaar en is geen partij bij de overeenkomsten die tussen Klanten en Vakmensen worden gesloten.',
      ],
    },
    {
      heading: '2. Definities',
      paragraphs: [
        '"Klant" is een gebruiker die opdrachten publiceert en op zoek is naar een dienstverlener.',
        '"Vakman" is een gebruiker die diensten aanbiedt en offertes indient op gepubliceerde opdrachten.',
        '"Opdracht" is een publicatie van een Klant met een beschrijving van de gevraagde dienst.',
        '"Offerte" is een voorstel van een Vakman voor een specifieke Opdracht.',
      ],
    },
    {
      heading: '3. Registratie en account',
      paragraphs: [
        'Om het Platform te gebruiken, moet u minimaal 18 jaar oud zijn en juiste en actuele gegevens verstrekken bij registratie.',
        'De gebruiker is verantwoordelijk voor het vertrouwelijk houden van zijn inloggegevens (e-mail en wachtwoord) en voor alle handelingen die via zijn account worden verricht.',
        'MasterBook behoudt zich het recht voor om registratie te weigeren of een account te deactiveren bij overtreding van deze voorwaarden.',
      ],
    },
    {
      heading: '4. Aard van de dienst',
      paragraphs: [
        'MasterBook biedt een technologisch platform voor het verbinden van Klanten en Vakmensen. Wij zijn geen partij bij de dienstenovereenkomst, garanderen niet de kwaliteit van het uitgevoerde werk en zijn niet verantwoordelijk voor geschillen tussen Klant en Vakman.',
        'Alle afspraken over prijs, termijnen, kwaliteit en uitvoeringswijze van de dienst worden rechtstreeks tussen de Klant en de Vakman overeengekomen.',
      ],
    },
    {
      heading: '5. Rechten en plichten van gebruikers',
      paragraphs: [
        'Gebruikers verplichten zich juiste informatie te verstrekken en geen illegale, beledigende of misleidende content te plaatsen, en het Platform niet te gebruiken voor andere doeleinden dan waarvoor het is bedoeld.',
        'Gebruikers verplichten zich te goeder trouw te communiceren en het Platform niet te omzeilen om eventuele toekomstige kosten te vermijden.',
      ],
    },
    {
      heading: '6. Kosten',
      paragraphs: [
        'Op dit moment is het gebruik van het Platform gratis voor Klanten en Vakmensen. MasterBook behoudt zich het recht voor om in de toekomst kosten in te voeren voor bepaalde functionaliteiten, waarover gebruikers vooraf worden geïnformeerd.',
      ],
    },
    {
      heading: '7. Door gebruikers gegenereerde content',
      paragraphs: [
        'Gebruikers zijn volledig verantwoordelijk voor de content die zij plaatsen (opdrachtbeschrijvingen, offertes, berichten, foto\'s, beoordelingen).',
        'MasterBook behoudt zich het recht voor om content te verwijderen die deze voorwaarden of toepasselijke wetgeving schendt, zonder voorafgaande kennisgeving.',
      ],
    },
    {
      heading: '8. Beperking van aansprakelijkheid',
      paragraphs: [
        'MasterBook is niet aansprakelijk voor directe of indirecte schade die voortvloeit uit het gebruik van het Platform, inclusief maar niet beperkt tot schade door niet-uitvoering van een dienst, vertraging, of een geschil tussen Klant en Vakman.',
        'Het Platform wordt aangeboden "as is", zonder garanties voor ononderbroken of foutloze werking.',
      ],
    },
    {
      heading: '9. Beëindiging van account',
      paragraphs: [
        'Een gebruiker kan op elk moment verzoeken om verwijdering van zijn account door contact op te nemen met de beheerder.',
        'MasterBook kan de toegang van een gebruiker beëindigen of beperken bij systematische overtredingen van deze voorwaarden, misbruik of illegale activiteiten.',
      ],
    },
    {
      heading: '10. Intellectueel eigendom',
      paragraphs: [
        'Alle rechten op het ontwerp, het logo, de software en de content van het Platform (met uitzondering van door gebruikers gegenereerde content) behoren toe aan MasterBook.',
      ],
    },
    {
      heading: '11. Wijzigingen van de voorwaarden',
      paragraphs: [
        'MasterBook behoudt zich het recht voor deze Algemene Voorwaarden te wijzigen. Wezenlijke wijzigingen worden aan gebruikers gecommuniceerd via het Platform of per e-mail.',
      ],
    },
    {
      heading: '12. Toepasselijk recht',
      paragraphs: [
        'Op deze Algemene Voorwaarden is Nederlands recht van toepassing, voor zover niet in strijd met dwingend toepasselijk EU-recht. Geschillen worden voorgelegd aan de bevoegde rechter in Nederland.',
      ],
    },
    {
      heading: '13. Contact',
      paragraphs: [
        'Voor vragen over deze Algemene Voorwaarden kunt u contact met ons opnemen via: support@masterbook.nl',
      ],
    },
  ],
  en: [
    {
      heading: '1. General provisions',
      paragraphs: [
        'These Terms of Service govern the relationship between MasterBook ("the Platform", "we") and the users who register and use the website masterbook.nl (hereinafter "Users").',
        'MasterBook is operated by [Owner name], trading as "MasterBook" (sole proprietorship / eenmanszaak, KVK number: [to be completed upon registration]), based in [address], the Netherlands.',
        'The Platform connects Clients seeking services with Professionals offering such services. MasterBook acts solely as an intermediary and is not a party to the contracts concluded between Clients and Professionals.',
      ],
    },
    {
      heading: '2. Definitions',
      paragraphs: [
        '"Client" is a user who publishes tasks and is looking for a service provider.',
        '"Professional" is a user who offers services and submits bids on published tasks.',
        '"Task" is a publication by a Client describing a requested service.',
        '"Bid" is a proposal by a Professional for a specific Task.',
      ],
    },
    {
      heading: '3. Registration and account',
      paragraphs: [
        'To use the Platform you must be at least 18 years old and provide accurate and up-to-date information when registering.',
        'The user is responsible for keeping their login credentials (email and password) confidential and for all actions carried out through their account.',
        'MasterBook reserves the right to refuse registration or deactivate an account in case of violation of these Terms.',
      ],
    },
    {
      heading: '4. Nature of the service',
      paragraphs: [
        'MasterBook provides a technology platform for connecting Clients and Professionals. We are not a party to the service agreement, do not guarantee the quality of the work performed, and are not responsible for disputes between a Client and a Professional.',
        'All arrangements regarding price, deadlines, quality and manner of performance of the service are agreed directly between the Client and the Professional.',
      ],
    },
    {
      heading: '5. Rights and obligations of users',
      paragraphs: [
        'Users undertake to provide truthful information, not to publish unlawful, offensive or misleading content, and not to use the Platform for purposes other than its intended purpose.',
        'Users undertake to communicate in good faith and not to bypass the Platform in order to avoid any future fees.',
      ],
    },
    {
      heading: '6. Fees',
      paragraphs: [
        'At present, use of the Platform is free of charge for Clients and Professionals. MasterBook reserves the right to introduce fees for certain features in the future, and users will be notified in advance.',
      ],
    },
    {
      heading: '7. User-generated content',
      paragraphs: [
        'Users are fully responsible for the content they publish (task descriptions, bids, messages, photos, reviews).',
        'MasterBook reserves the right to remove content that violates these Terms or applicable law, without prior notice.',
      ],
    },
    {
      heading: '8. Limitation of liability',
      paragraphs: [
        'MasterBook is not liable for direct or indirect damages arising from the use of the Platform, including but not limited to damages from non-performance of a service, delay, or a dispute between a Client and a Professional.',
        'The Platform is provided "as is", without warranties of uninterrupted or error-free operation.',
      ],
    },
    {
      heading: '9. Termination of account',
      paragraphs: [
        'A user may request deletion of their account at any time by contacting the administrator.',
        'MasterBook may terminate or restrict a user\'s access in case of systematic violations of these Terms, abuse, or illegal activity.',
      ],
    },
    {
      heading: '10. Intellectual property',
      paragraphs: [
        'All rights to the design, logo, software and content of the Platform (excluding user-generated content) belong to MasterBook.',
      ],
    },
    {
      heading: '11. Changes to the Terms',
      paragraphs: [
        'MasterBook reserves the right to amend these Terms of Service. Material changes will be communicated to users via the Platform or by email.',
      ],
    },
    {
      heading: '12. Governing law',
      paragraphs: [
        'These Terms of Service are governed by the laws of the Netherlands, insofar as this does not conflict with mandatory applicable EU law. Disputes shall be submitted to the competent court in the Netherlands.',
      ],
    },
    {
      heading: '13. Contact',
      paragraphs: [
        'For questions about these Terms of Service, please contact us at: support@masterbook.nl',
      ],
    },
  ],
  bg: [
    {
      heading: '1. Общи разпоредби',
      paragraphs: [
        'Настоящите Общи условия уреждат отношенията между MasterBook („Платформата", „ние") и потребителите, които се регистрират и използват уебсайта masterbook.nl (наричани по-долу „Потребители").',
        'MasterBook се управлява от [Име на собственика], търгуващ под името „MasterBook" (едноличен търговец / eenmanszaak, KVK номер: [ще се попълни при регистрация]), със седалище в [адрес], Нидерландия.',
        'Платформата свързва Клиенти, търсещи услуги, с Професионалисти, предлагащи такива услуги. MasterBook действа единствено като посредник и не е страна по договорите, сключвани между Клиенти и Професионалисти.',
      ],
    },
    {
      heading: '2. Определения',
      paragraphs: [
        '„Клиент" е потребител, който публикува задачи и търси изпълнител на услуга.',
        '„Професионалист" е потребител, който предлага услуги и подава оферти по публикувани задачи.',
        '„Задача" е публикация от Клиент, описваща търсена услуга.',
        '„Оферта" е предложение от Професионалист по конкретна Задача.',
      ],
    },
    {
      heading: '3. Регистрация и профил',
      paragraphs: [
        'За да използвате Платформата, трябва да сте на възраст 18 или повече години и да предоставите точна и актуална информация при регистрация.',
        'Потребителят носи отговорност за опазването на конфиденциалността на своите данни за достъп (имейл и парола) и за всички действия, извършени чрез неговия профил.',
        'MasterBook си запазва правото да откаже регистрация или да деактивира профил при нарушение на настоящите условия.',
      ],
    },
    {
      heading: '4. Естество на услугата',
      paragraphs: [
        'MasterBook предоставя технологична платформа за свързване на Клиенти и Професионалисти. Ние не сме страна по договора за услуга, не гарантираме качеството на извършената работа и не носим отговорност за спорове между Клиент и Професионалист.',
        'Всички договорености относно цена, срокове, качество и начин на изпълнение на услугата се уговарят пряко между Клиента и Професионалиста.',
      ],
    },
    {
      heading: '5. Права и задължения на потребителите',
      paragraphs: [
        'Потребителите се задължават да предоставят вярна информация, да не публикуват незаконно, обидно или подвеждащо съдържание и да не използват Платформата за цели, различни от нейното предназначение.',
        'Потребителите се задължават да общуват добросъвестно и да не заобикалят Платформата с цел избягване на евентуални бъдещи такси.',
      ],
    },
    {
      heading: '6. Такси',
      paragraphs: [
        'Към момента използването на Платформата е безплатно за Клиенти и Професионалисти. MasterBook си запазва правото да въведе такси за определени функционалности в бъдеще, за което потребителите ще бъдат предварително уведомени.',
      ],
    },
    {
      heading: '7. Съдържание, генерирано от потребители',
      paragraphs: [
        'Потребителите носят пълна отговорност за съдържанието, което публикуват (описания на задачи, оферти, съобщения, снимки, отзиви).',
        'MasterBook си запазва правото да премахва съдържание, нарушаващо настоящите условия или приложимото законодателство, без предварително уведомление.',
      ],
    },
    {
      heading: '8. Ограничение на отговорност',
      paragraphs: [
        'MasterBook не носи отговорност за преки или непреки вреди, произтичащи от използването на Платформата, включително, но не само, вреди от неизпълнение на услуга, забавяне, или спор между Клиент и Професионалист.',
        'Платформата се предоставя „както е", без гаранции за непрекъсваемост или безпогрешност на работата ѝ.',
      ],
    },
    {
      heading: '9. Прекратяване на профил',
      paragraphs: [
        'Потребител може да заяви изтриване на своя профил по всяко време чрез контакт с администратора.',
        'MasterBook може да прекрати или ограничи достъпа на потребител при системни нарушения на настоящите условия, злоупотреба или незаконна дейност.',
      ],
    },
    {
      heading: '10. Интелектуална собственост',
      paragraphs: [
        'Всички права върху дизайна, логото, софтуера и съдържанието на Платформата (с изключение на съдържанието, генерирано от потребителите) принадлежат на MasterBook.',
      ],
    },
    {
      heading: '11. Изменения на условията',
      paragraphs: [
        'MasterBook си запазва правото да изменя настоящите Общи условия. Съществени промени ще бъдат съобщавани на потребителите чрез Платформата или по имейл.',
      ],
    },
    {
      heading: '12. Приложимо право',
      paragraphs: [
        'Настоящите Общи условия се уреждат от законодателството на Нидерландия, доколкото не противоречи на приложимото право на Европейския съюз. Споровете се отнасят до компетентния съд в Нидерландия.',
      ],
    },
    {
      heading: '13. Контакти',
      paragraphs: [
        'При въпроси относно настоящите Общи условия можете да се свържете с нас на имейл: support@masterbook.nl',
      ],
    },
  ],
  tr: [
    {
      heading: '1. Genel hükümler',
      paragraphs: [
        'Bu Kullanım Koşulları, MasterBook ("Platform", "biz") ile masterbook.nl web sitesine kayıt olan ve onu kullanan kullanıcılar (bundan sonra "Kullanıcılar") arasındaki ilişkiyi düzenler.',
        'MasterBook, [Sahip adı] tarafından "MasterBook" ticari unvanıyla işletilir (eenmanszaak, KVK numarası: [kayıt sonrası doldurulacak]), [adres], Hollanda adresinde yerleşiktir.',
        'Platform, hizmet arayan Müşterileri bu hizmetleri sunan Profesyonellerle bir araya getirir. MasterBook yalnızca aracıdır ve Müşteriler ile Profesyoneller arasında yapılan sözleşmelerin tarafı değildir.',
      ],
    },
    {
      heading: '2. Tanımlar',
      paragraphs: [
        '"Müşteri", görev yayımlayan ve hizmet sağlayıcı arayan kullanıcıdır.',
        '"Profesyonel", hizmet sunan ve yayımlanan görevlere teklif veren kullanıcıdır.',
        '"Görev", Müşterinin talep edilen hizmeti anlattığı yayındır.',
        '"Teklif", bir Profesyonelin belirli bir Görev için önerisidir.',
      ],
    },
    {
      heading: '3. Kayıt ve hesap',
      paragraphs: [
        'Platformu kullanmak için en az 18 yaşında olmalı ve kayıt sırasında doğru ve güncel bilgiler vermelisiniz.',
        'Kullanıcı, giriş bilgilerinin (e-posta ve şifre) gizliliğinden ve hesabı üzerinden yapılan tüm işlemlerden sorumludur.',
        'MasterBook, bu koşulların ihlalinde kaydı reddetme veya hesabı devre dışı bırakma hakkını saklı tutar.',
      ],
    },
    {
      heading: '4. Hizmetin niteliği',
      paragraphs: [
        'MasterBook, Müşteriler ile Profesyonelleri bir araya getiren teknolojik bir platform sunar. Hizmet sözleşmesinin tarafı değiliz, yapılan işin kalitesini garanti etmeyiz ve Müşteri ile Profesyonel arasındaki uyuşmazlıklardan sorumlu değiliz.',
        'Fiyat, süre, kalite ve yürütme şekline ilişkin tüm anlaşmalar doğrudan Müşteri ile Profesyonel arasında yapılır.',
      ],
    },
    {
      heading: '5. Kullanıcıların hak ve yükümlülükleri',
      paragraphs: [
        'Kullanıcılar doğru bilgi vermeyi, yasa dışı, saldırgan veya yanıltıcı içerik yayımlamamayı ve Platformu amacı dışında kullanmamayı kabul eder.',
        'Kullanıcılar iyi niyetle iletişim kurmayı ve olası gelecekteki ücretlerden kaçınmak için Platformu atlamamayı kabul eder.',
      ],
    },
    {
      heading: '6. Ücretler',
      paragraphs: [
        'Şu anda Platformun kullanımı Müşteriler ve Profesyoneller için ücretsizdir. MasterBook, belirli işlevler için ileride ücret getirme hakkını saklı tutar; kullanıcılar önceden bilgilendirilir.',
      ],
    },
    {
      heading: '7. Kullanıcı tarafından üretilen içerik',
      paragraphs: [
        'Kullanıcılar yayımladıkları içerikten (görev açıklamaları, teklifler, mesajlar, fotoğraflar, yorumlar) tam olarak sorumludur.',
        'MasterBook, bu koşulları veya yürürlükteki mevzuatı ihlal eden içeriği önceden bildirimde bulunmadan kaldırma hakkını saklı tutar.',
      ],
    },
    {
      heading: '8. Sorumluluğun sınırlanması',
      paragraphs: [
        'MasterBook, Platformun kullanımından doğan doğrudan veya dolaylı zararlardan, hizmetin yerine getirilmemesi, gecikme veya Müşteri ile Profesyonel arasındaki uyuşmazlık dahil olmak üzere sorumlu değildir.',
        'Platform "olduğu gibi" sunulur; kesintisiz veya hatasız çalışma garantisi verilmez.',
      ],
    },
    {
      heading: '9. Hesabın sona erdirilmesi',
      paragraphs: [
        'Kullanıcı, yöneticiyle iletişime geçerek hesabının silinmesini her an talep edebilir.',
        'MasterBook, bu koşulların sistematik ihlali, kötüye kullanım veya yasa dışı faaliyet halinde erişimi sonlandırabilir veya kısıtlayabilir.',
      ],
    },
    {
      heading: '10. Fikri mülkiyet',
      paragraphs: [
        'Platformun tasarımı, logosu, yazılımı ve içeriğine (kullanıcı içeriği hariç) ilişkin tüm haklar MasterBook\'a aittir.',
      ],
    },
    {
      heading: '11. Koşullardaki değişiklikler',
      paragraphs: [
        'MasterBook bu Kullanım Koşullarını değiştirme hakkını saklı tutar. Önemli değişiklikler Platform veya e-posta yoluyla kullanıcılara bildirilir.',
      ],
    },
    {
      heading: '12. Uygulanacak hukuk',
      paragraphs: [
        'Bu Kullanım Koşulları, zorunlu AB hukuku ile çelişmediği sürece Hollanda hukukuna tabidir. Uyuşmazlıklar Hollanda\'daki yetkili mahkemeye sunulur.',
      ],
    },
    {
      heading: '13. İletişim',
      paragraphs: [
        'Bu Kullanım Koşulları hakkında sorularınız için: support@masterbook.nl',
      ],
    },
  ],
  pl: [
    {
      heading: '1. Postanowienia ogólne',
      paragraphs: [
        'Niniejszy Regulamin określa relację między MasterBook („Platforma", „my") a użytkownikami, którzy rejestrują się i korzystają z witryny masterbook.nl (dalej „Użytkownicy").',
        'MasterBook jest prowadzony przez [Imię właściciela] pod nazwą „MasterBook" (eenmanszaak, numer KVK: [do uzupełnienia po rejestracji]), z siedzibą pod adresem [adres], Holandia.',
        'Platforma łączy Klientów szukających usług z Profesjonalistami, którzy je oferują. MasterBook działa wyłącznie jako pośrednik i nie jest stroną umów zawieranych między Klientami a Profesjonalistami.',
      ],
    },
    {
      heading: '2. Definicje',
      paragraphs: [
        '„Klient" to użytkownik publikujący zadania i szukający wykonawcy.',
        '„Profesjonalista" to użytkownik oferujący usługi i składający oferty do opublikowanych zadań.',
        '„Zadanie" to publikacja Klienta opisująca potrzebną usługę.',
        '„Oferta" to propozycja Profesjonalisty do konkretnego Zadania.',
      ],
    },
    {
      heading: '3. Rejestracja i konto',
      paragraphs: [
        'Aby korzystać z Platformy, musisz mieć co najmniej 18 lat i podać prawdziwe, aktualne dane przy rejestracji.',
        'Użytkownik odpowiada za poufność danych logowania (e-mail i hasło) oraz za wszystkie działania wykonane przez jego konto.',
        'MasterBook zastrzega sobie prawo do odmowy rejestracji lub dezaktywacji konta w razie naruszenia niniejszego regulaminu.',
      ],
    },
    {
      heading: '4. Charakter usługi',
      paragraphs: [
        'MasterBook udostępnia platformę technologiczną do łączenia Klientów i Profesjonalistów. Nie jesteśmy stroną umowy o usługę, nie gwarantujemy jakości wykonanej pracy i nie odpowiadamy za spory między Klientem a Profesjonalistą.',
        'Wszelkie ustalenia dotyczące ceny, terminów, jakości i sposobu wykonania usługi zawierane są bezpośrednio między Klientem a Profesjonalistą.',
      ],
    },
    {
      heading: '5. Prawa i obowiązki użytkowników',
      paragraphs: [
        'Użytkownicy zobowiązują się podawać prawdziwe informacje, nie publikować treści niezgodnych z prawem, obraźliwych lub wprowadzających w błąd oraz nie używać Platformy niezgodnie z jej przeznaczeniem.',
        'Użytkownicy zobowiązują się komunikować w dobrej wierze i nie omijać Platformy w celu uniknięcia ewentualnych przyszłych opłat.',
      ],
    },
    {
      heading: '6. Opłaty',
      paragraphs: [
        'Obecnie korzystanie z Platformy jest bezpłatne dla Klientów i Profesjonalistów. MasterBook zastrzega sobie prawo do wprowadzenia opłat za wybrane funkcje w przyszłości, o czym użytkownicy zostaną wcześniej poinformowani.',
      ],
    },
    {
      heading: '7. Treści tworzone przez użytkowników',
      paragraphs: [
        'Użytkownicy ponoszą pełną odpowiedzialność za publikowane treści (opisy zadań, oferty, wiadomości, zdjęcia, recenzje).',
        'MasterBook zastrzega sobie prawo do usuwania treści naruszających niniejszy regulamin lub obowiązujące prawo bez wcześniejszego powiadomienia.',
      ],
    },
    {
      heading: '8. Ograniczenie odpowiedzialności',
      paragraphs: [
        'MasterBook nie ponosi odpowiedzialności za szkody bezpośrednie lub pośrednie wynikające z korzystania z Platformy, w tym za niewykonanie usługi, opóźnienie lub spór między Klientem a Profesjonalistą.',
        'Platforma jest udostępniana „tak jak jest", bez gwarancji ciągłości lub bezbłędnego działania.',
      ],
    },
    {
      heading: '9. Zakończenie konta',
      paragraphs: [
        'Użytkownik może w każdej chwili poprosić o usunięcie konta, kontaktując się z administratorem.',
        'MasterBook może zakończyć lub ograniczyć dostęp użytkownika w razie systematycznego naruszenia regulaminu, nadużycia lub działalności niezgodnej z prawem.',
      ],
    },
    {
      heading: '10. Własność intelektualna',
      paragraphs: [
        'Wszelkie prawa do projektu, logo, oprogramowania i treści Platformy (z wyjątkiem treści tworzonych przez użytkowników) przysługują MasterBook.',
      ],
    },
    {
      heading: '11. Zmiany regulaminu',
      paragraphs: [
        'MasterBook zastrzega sobie prawo do zmiany niniejszego Regulaminu. Istotne zmiany zostaną przekazane użytkownikom przez Platformę lub e-mailem.',
      ],
    },
    {
      heading: '12. Prawo właściwe',
      paragraphs: [
        'Niniejszy Regulamin podlega prawu holenderskiemu, o ile nie jest to sprzeczne z bezwzględnie obowiązującym prawem UE. Spory trafiają do właściwego sądu w Holandii.',
      ],
    },
    {
      heading: '13. Kontakt',
      paragraphs: [
        'W sprawach dotyczących niniejszego Regulaminu skontaktuj się z nami: support@masterbook.nl',
      ],
    },
  ],
};

const SUPPORTED_LANGS: LangKey[] = ['bg', 'en', 'nl', 'tr', 'pl'];

const Terms: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] as LangKey;
  const lang: LangKey = SUPPORTED_LANGS.includes(currentLang) ? currentLang : 'nl';
  const sections = content[lang];

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 3, sm: 6 }, mb: 6, px: { xs: 2, sm: 3 } }}>
      <PageBackButton />
      <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('legal.terms_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('legal.last_updated')}: {LAST_UPDATED}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          {t('legal.language_notice')}
        </Alert>

        {sections.map((section, idx) => (
          <Box key={section.heading} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {section.heading}
            </Typography>
            {section.paragraphs.map((p, pIdx) => (
              <Typography key={pIdx} variant="body1" sx={{ mb: 1, color: 'text.primary' }}>
                {p}
              </Typography>
            ))}
            {idx < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Paper>
    </Container>
  );
};

export default Terms;
